import { useEffect, useMemo, useState } from "react";
import { Activity, HeartPulse, Stethoscope } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../lib/ui";
import { ScrollRail } from "./ScrollRail";

const usd = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : `$${Math.round(n || 0).toLocaleString()}`);
const int = (n) => Math.round(n || 0).toLocaleString();

const Stat = ({ label, value, note, testId }) => (
  <div className="bg-[#0b0b0b] p-6" data-testid={testId}>
    <p className="overline !text-zinc-500">{label}</p>
    <p className="mono mt-3 text-2xl font-bold text-ochre md:text-3xl">{value}</p>
    {note ? <p className="mt-2 text-[0.68rem] uppercase tracking-[0.14em] text-zinc-400">{note}</p> : null}
  </div>
);

export const HealthCase = ({ heading = "Fewer people in hospital, fewer days lost" }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [pct, setPct] = useState(100);

  useEffect(() => {
    api.get("/health-case").then((r) => setD(r.data)).catch(() => setErr("We could not load the health case. Refresh to try again."));
  }, []);

  const scenario = useMemo(() => d?.scenarios?.find((s) => s.completion_pct === pct) || null, [d, pct]);

  if (err)
    return (
      <p className="mt-16 border border-maasai/40 bg-maasai/10 p-6 text-sm text-white" data-testid="health-case-error">
        {err}
      </p>
    );
  if (!d || !scenario) return null;
  const p = d.per_participant;

  return (
    <section className="mt-20 border-t border-zinc-900 pt-14" data-testid="health-case">
      <Reveal>
        <p className="eyebrow">Health, fitness and productivity</p>
        <h2 className="display-2 mt-4 max-w-4xl">{heading}</h2>
        <p className="body-copy mt-6 max-w-3xl">
          Non-communicable disease has climbed from 27% to about 39% of Kenyan deaths in a decade and already
          accounts for more than half of hospital admissions. Pneumonia has been the leading registered cause of
          death in Kenyan health facilities for four straight years. Only 5% of the physical activity Kenyans do is
          recreational — the rest is work and transport, and both disappear when a young person moves to a city and
          takes a desk. Football is the cheapest way a state can move a large number of people, often enough, for
          long enough, to bend those curves.
        </p>
      </Reveal>

      <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="health-scenarios">
        <span className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-400">Programme completed</span>
        {d.scenarios.map((s) => (
          <button
            key={s.completion_pct}
            type="button"
            onClick={() => setPct(s.completion_pct)}
            data-testid={`health-scenario-${s.completion_pct}`}
            className={`border px-4 py-2 text-[0.62rem] font-bold uppercase tracking-[0.16em] transition-colors ${
              pct === s.completion_pct ? "border-maasai bg-maasai text-white" : "border-white/20 text-zinc-300 hover:border-ochre"
            }`}
          >
            {s.completion_pct}%
          </button>
        ))}
      </div>

      <div className="mt-8 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="health-headline">
        <Stat label="Players moving every week" value={int(scenario.players)} note={`${int(scenario.active_minutes_per_year / 1e6)}m active minutes a year`} testId="health-players" />
        <Stat label="Hospital admissions avoided a year" value={int(scenario.admissions_avoided)} note="NCD and respiratory admissions" testId="health-admissions" />
        <Stat label="Sick days recovered a year" value={int(scenario.sick_days_avoided)} note={`${usd(scenario.productivity_usd)} of output`} testId="health-sickdays" />
        <Stat label="Premature deaths delayed a year" value={scenario.premature_deaths_delayed} note="Inactivity-attributable NCD deaths" testId="health-deaths" />
        <Stat label="Off the health system" value={usd(scenario.health_system_usd)} note="Treatment and admissions avoided" testId="health-system" />
        <Stat label="Back to employers" value={usd(scenario.productivity_usd)} note="Absence and presenteeism" testId="health-productivity" />
        <Stat label="Combined health and productivity value" value={usd(scenario.total_usd)} note={`$${scenario.per_dollar_built} per dollar built`} testId="health-total" />
        <Stat label="Per player, per year" value={`$${p.total_usd}`} note={`$${p.health_system_usd} health, $${p.productivity_usd} productivity`} testId="health-per-player" />
      </div>
      <p className="mt-4 max-w-4xl text-xs leading-relaxed text-zinc-400" data-testid="health-note">{scenario.note}</p>

      <h3 className="display-3 mt-16">Kenya&rsquo;s own numbers</h3>
      <ScrollRail testId="health-facts" className="mt-8" innerClassName="flex snap-x snap-mandatory gap-px bg-zinc-800 border border-zinc-800">
        {d.facts.map((f, i) => (
          <div
            key={f.label}
            className="flex min-w-[82%] snap-start flex-col justify-between bg-[#0b0b0b] p-7 sm:min-w-[48%] lg:min-w-[30%]"
            data-testid={`health-fact-${i}`}
          >
            <p className="mono text-2xl font-bold text-maasai md:text-3xl">{f.value}</p>
            <p className="mt-4 font-display text-base uppercase leading-tight tracking-tight text-white">{f.label}</p>
            <p className="mt-3 text-xs leading-relaxed text-zinc-400">{f.detail}</p>
            <a
              data-testid={`health-fact-source-${i}`}
              href={f.url}
              target="_blank"
              rel="noreferrer"
              className="mono mt-5 text-[0.58rem] uppercase tracking-[0.16em] text-ochre underline underline-offset-4 hover:text-white"
            >
              {f.source}
            </a>
          </div>
        ))}
      </ScrollRail>

      <h3 className="display-3 mt-16">How football helps</h3>
      <div className="mt-8 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-2 lg:grid-cols-3" data-testid="health-mechanisms">
        {d.how_football_helps.map((h, i) => (
          <div key={h.title} className="bg-[#0b0b0b] p-7" data-testid={`health-mechanism-${i}`}>
            {[<HeartPulse key="a" size={16} />, <Stethoscope key="b" size={16} />, <Activity key="c" size={16} />][i % 3]}
            <p className="mt-4 font-display text-lg uppercase leading-tight tracking-tight text-white">{h.title}</p>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">{h.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 overflow-x-auto border border-zinc-800" data-testid="health-table">
        <table className="w-full min-w-[860px] border-collapse text-left">
          <thead>
            <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
              {["Completion", "Counties", "Players", "Admissions avoided", "Sick days recovered", "Deaths delayed", "Health system", "Productivity", "Combined"].map((h) => (
                <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.scenarios.map((s) => (
              <tr key={s.completion_pct} className="border-b border-zinc-900" data-testid={`health-row-${s.completion_pct}`}>
                <td className="mono px-4 py-3 text-sm text-maasai">{s.completion_pct}%</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{s.counties}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(s.players)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(s.admissions_avoided)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(s.sick_days_avoided)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{s.premature_deaths_delayed}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(s.health_system_usd)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(s.productivity_usd)}</td>
                <td className="mono px-4 py-3 text-sm text-ochre">{usd(s.total_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div className="border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="health-method">
          <p className="overline text-ochre">The method</p>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300">{d.method}</p>
        </div>
        <div className="border border-white/10 bg-[#0b0b0b] p-6" data-testid="health-sources">
          <p className="overline">Sources</p>
          <div className="mt-4 flex flex-col gap-2 text-xs text-zinc-300">
            {d.sources.map((s, i) => (
              <a
                key={s.url}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`health-source-${i}`}
                className="underline underline-offset-4 hover:text-ochre"
              >
                {s.label} — {s.source}
              </a>
            ))}
          </div>
          <p className="mt-5 text-xs leading-relaxed text-zinc-400">
            Health statistics are Kenya&rsquo;s own published figures. The effect sizes applied to them are our
            conservative estimates, taken from WHO&rsquo;s physical activity economics work and published so they can
            be argued with. Wellbeing and social value are priced in the grassroots model, never twice.
          </p>
        </div>
      </div>
    </section>
  );
};
